import { MdKeyboardArrowDown, MdDragIndicator } from 'react-icons/md'
import { useUiFromSpecContext } from '../../EditorContextProvider'
import { IoIosSettings } from 'react-icons/io'
import { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { useDrag, useDrop } from 'react-dnd'
import { ItemTypes, ALL_ITEM_TYPES } from '../dndTypes'
import { getDisplayName } from './utils/getDisplayName'
import { getIcon } from './utils/getIcon'
import { handleElementSelection } from './utils/handleElementSelection'
import { handleSelectionClick } from './utils/handleSelectionClick'
import { containsMultiXPath } from './utils/containsMultiXPath'

export interface DropResult {
  xpath: string
  index: number
  uiType?: string
  targetComponent?: any
  parentTargetComponent?: any
  fromDropper?: boolean
}

export default function LayerPanel() {
  const { currentSpec } = useUiFromSpecContext()
  const [key, setKey] = useState(0)
  const xpath = ''

  useEffect(() => {
    // Force re-render by updating a dummy state on currentSpec change
    // (Not usually needed, as currentSpec is already used in render)
    setKey((prevKey) => prevKey + 1)
  }, [currentSpec])

  return (
    <div className="h-[calc(100vh-240px)] overflow-y-auto" key={key}>
      {currentSpec?.map((component, index) => (
        <ComponentItem key={index} xpath={xpath + index} component={component} />
      ))}
    </div>
  )
}

const ComponentItem = ({ component, xpath, index = 0, level = 0, parentComponent, isParentSelected }: any) => {
  const {
    currentSpec,
    setSelectedElement,
    setSelectedPath,
    setShow,
    reorderElementsInSpec,
    reorderMultipleElementsInSpec,
    language,
    cannotDragElements,
    disableLayerDropper,
    customDisplayNameLayer,
    customDropper,
    customReOrder,
    customIconLayer,
    selectedElements,
    setSelectedElements,
  } = useUiFromSpecContext()
  const [isOpen, setIsOpen] = useState(true)
  const hasChildren = component?.children && component?.children?.length > 0
  const ref = useRef<HTMLDivElement>(null)

  const handleToggle = () => {
    if (hasChildren) {
      setIsOpen(!isOpen)
    }
  }

  const canDragThis = !cannotDragElements?.includes(component.ui_type)

  // drag logic — only CANVAS_ELEMENT type; uses explicit drag handle
  const [{ isDragging }, connectDrag, connectDragPreview] = useDrag(
    () => ({
      type: ItemTypes.CANVAS_ELEMENT,
      item: { ...component, originalXpath: xpath },
      canDrag: canDragThis,
      end: (item, monitor) => {
        const dropResult = monitor.getDropResult<DropResult>()

        // AddChildItem renders *after* item at index N and returns index=N → insert at N+1.
        // ChildDropper already sends the correct insert position.
        const insertIndex = dropResult?.fromDropper ? (dropResult.index + 1) : dropResult?.index
        const toIndex = dropResult?.xpath + '.' + insertIndex

        if (!item || !dropResult || !toIndex || component === dropResult?.targetComponent) {
          return
        }

        if (!canDragThis) {
          console.warn("cannotDragElements includes this component's type")
          return
        }

        // Only allow drops on valid container ui types or dedicated ChildDroppers
        if ((!dropResult?.uiType && !dropResult?.fromDropper) || !customDropper?.includes(dropResult?.uiType ?? '')) {
          console.warn("doesn't have a valid drop target")
          return
        }

        // Prevent dropping a parent into its own children
        const originalPath = item.originalXpath || xpath
        if (dropResult.xpath.startsWith(originalPath + '.')) {
          console.warn('Cannot move a component into its own children')
          return
        }

        if (customReOrder) {
          customReOrder({
            item,
            dropResult,
            selectedElements,
            fromIndex: xpath.split('.'),
            toIndex: toIndex.split('.'),
            defaultReOrder: reorderElementsInSpec,
            defaultMultipleReOrder: reorderMultipleElementsInSpec,
            parentComponent,
            index,
          })
        } else if (
            selectedElements &&
            selectedElements.length > 0 &&
            containsMultiXPath(selectedElements, xpath.split('.'))
          ) {
            reorderMultipleElementsInSpec(selectedElements, toIndex.split('.'))
          } else {
            reorderElementsInSpec(xpath.split('.'), toIndex.split('.'))
          }
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
        handlerId: monitor.getHandlerId(),
      }),
    }),
    [selectedElements, xpath, component, canDragThis]
  )

  // drop logic — accept both palette and canvas drags
  const [{ canDrop, isOver }, connectDrop] = useDrop(
    () => ({
      accept: ALL_ITEM_TYPES,
      canDrop: (item: any) => {
        // Prevent dropping a parent into its own children
        const originalPath = item.originalXpath || ''
        if (originalPath && xpath.startsWith(originalPath + '.')) {
          return false
        }
        return true
      },
      drop: (_item, monitor) => {
        // Only handle if not already handled by a child drop target
        if (monitor.didDrop()) {
          return
        }

        return {
          xpath,
          index,
          uiType: component.ui_type,
          targetComponent: component,
          parentTargetComponent: parentComponent,
        } as DropResult
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [xpath, index, component, parentComponent]
  )

  const isOnDrop = canDrop && isOver
  const isSelected = (selectedElements ?? []).some((x) => x.join('.') === xpath)

  const disableDropper = () => {
    if (disableLayerDropper?.includes(component.ui_type) || !component.ui_type || !component.children) {
      return false
    }
    return true
  }

  // Attach drag preview + drop to the whole row
  connectDragPreview(ref)
  connectDrop(ref)

  // Callback refs: attach drag source to whichever handle element is mounted
  const leftHandleRef = useCallback(
    (el: HTMLDivElement | null) => { if (canDragThis) connectDrag(el) },
    [connectDrag, canDragThis]
  )
  const rightHandleRef = useCallback(
    (el: HTMLDivElement | null) => { if (canDragThis) connectDrag(el) },
    [connectDrag, canDragThis]
  )

  return (
    <div
      style={{ paddingLeft: `${level > 0 ? 1.25 : 0}rem` }}
      className={clsx('relative transition-opacity duration-150', isDragging && 'opacity-40')}
      ref={ref}
    >
      <div
        className={clsx(
          'flex items-center group py-1 rounded-md hover:bg-slate-50',
          isOnDrop && 'bg-blue-50 ring-1 ring-blue-300',
          (isParentSelected || isSelected) && 'bg-blue-50 hover:bg-blue-50',
          canDragThis ? 'cursor-pointer' : 'cursor-default'
        )}
        onMouseEnter={(event) => {
          handleElementSelection(event, false, xpath, currentSpec, setSelectedElement, setSelectedPath, setShow)
        }}
        onClick={(event) => {
          if (cannotDragElements?.includes(component.ui_type)) {
            return
          }
          handleSelectionClick({ event, xpath, currentSpec, selectedElements, setSelectedElements })
        }}
      >
        {/* Left drag handle */}
        {canDragThis ? (
          <div
            ref={leftHandleRef}
            className="flex-none w-4 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors cursor-grab active:cursor-grabbing"
            title="Drag to move"
            onClick={(e) => e.stopPropagation()}
          >
            <MdDragIndicator className="text-sm" />
          </div>
        ) : (
          <div className="w-4" />
        )}
        {/* Expand/collapse toggle */}
        {hasChildren ? (
          <button
            className="flex-none w-5 h-5 flex items-center justify-center hover:bg-slate-100 rounded-md"
            onClick={(e) => { e.stopPropagation(); handleToggle() }}
          >
            <MdKeyboardArrowDown className={`transition-transform ${!isOpen ? '-rotate-90' : ''}`} />
          </button>
        ) : (
          <div className="w-5"> </div>
        )}
        <div className="flex flex-1 min-w-0">
          <div className="flex-none w-4">
            {customIconLayer
              ? customIconLayer(component?.ui_type, getIcon(component?.ui_type))
              : getIcon(component?.ui_type)}
          </div>
          <div className="flex-1 flex gap-1 items-center min-w-0">
            <span className={clsx('text-xs line-clamp-2 ml-1 max-w-[140px] truncate', isSelected && 'text-blue-600')}>
              {customDisplayNameLayer
                ? customDisplayNameLayer({
                    component,
                    index,
                    language,
                    parentComponent,
                    default: getDisplayName({ component, index, language, parentComponent }),
                  })
                : getDisplayName({ component, index, language, parentComponent })}
            </span>
            <div
              className="flex-none w-4 hidden group-hover:block text-slate-500 hover:text-blue-500"
              onClick={(event) =>
                handleElementSelection(event, true, xpath, currentSpec, setSelectedElement, setSelectedPath, setShow)
              }
            >
              <IoIosSettings />
            </div>
          </div>
        </div>
        {/* Right-side drag handle — muncul saat hover */}
        {canDragThis && (
          <div
            ref={rightHandleRef}
            className="flex-none ml-1 w-5 h-full flex items-center justify-center text-slate-400 opacity-0 group-hover:opacity-100 hover:text-slate-600 transition-all cursor-grab active:cursor-grabbing"
            title="Drag to move"
            onClick={(e) => e.stopPropagation()}
          >
            <MdDragIndicator className="text-sm" />
          </div>
        )}
      </div>
      {isOpen && hasChildren && (
        <div>
          {component?.children?.map((child: any, index: number) => {
            return (
              <>
                {index === 0 && disableDropper() && <ChildDropper xpath={xpath} index={index}></ChildDropper>}
                <ComponentItem
                  key={index}
                  index={index}
                  xpath={`${xpath}.${index}`}
                  parentUiType={component.ui_type}
                  component={child}
                  parentComponent={component}
                  isParentSelected={isParentSelected || isSelected || false}
                  level={level + 1}
                />
                {disableDropper() && <ChildDropper xpath={xpath} index={index + 1}></ChildDropper>}
              </>
            )
          })}
        </div>
      )}
    </div>
  )
}

const ChildDropper = ({ xpath, index }: DropResult) => {
  const ref = useRef<HTMLDivElement>(null)
  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      // Accept BOTH palette (new element) and canvas (reorder) drags
      accept: ALL_ITEM_TYPES,
      canDrop: (item: any) => {
        // Prevent dropping a parent into its own children
        const originalPath = item.originalXpath || ''
        if (originalPath && xpath.startsWith(originalPath)) {
          return false
        }
        return true
      },
      drop: (_item, monitor) => {
        // Propagation already stopped by nested child drops
        if (monitor.didDrop()) return
        return { xpath, index, uiType: 'dropper', fromDropper: true } as DropResult
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [xpath, index]
  )

  const isActive = canDrop && isOver
  const isInvalidDrop = isOver && !canDrop
  drop(ref)

  return (
    <div
      ref={ref}
      className={clsx(
        'relative action-indicator transition-all duration-200 ease-in-out',
        isActive ? 'h-4 my-0.5' : 'h-2'
      )}
    >
      <div
        className={clsx(
          'add-item-action absolute flex items-center top-0 text-center left-0 w-full h-full z-10 rounded-full',
          {
            'opacity-100 visible': isActive,
            'opacity-0 invisible': !isActive && !isInvalidDrop,
          }
        )}
        data-testid="child-dropper"
      >
        <span
          className={clsx(
            'h-1 w-full rounded-full transition-colors',
            isActive ? 'bg-blue-500 shadow-sm' : 'bg-slate-400'
          )}
        />
      </div>
    </div>
  )
}
